/**
 * Environment Variables Utility
 * Provides structured access to environment variables and API keys
 */

/**
 * Debug configuration
 * Controls console logging throughout the application
 *
 * DEFAULT BEHAVIOR: Debug logging is DISABLED unless explicitly enabled
 * - Missing REACT_APP_DEBUG_MODE → false (disabled)
 * - REACT_APP_DEBUG_MODE='' → false (disabled)
 * - REACT_APP_DEBUG_MODE=false → false (disabled)
 * - REACT_APP_DEBUG_MODE=true → true (enabled)
 */
export const debug = {
  // Master debug switch - DEFAULTS TO FALSE (disabled)
  // Only enables when explicitly set to string 'true'
  enabled: process.env.REACT_APP_DEBUG_MODE === 'true' || false,

  // Granular debug controls (all require master switch to be on)
  // These default to TRUE when master is enabled, but can be individually disabled
  app: process.env.REACT_APP_DEBUG_APP !== 'false', // App initialization
  luna: process.env.REACT_APP_DEBUG_LUNA !== 'false', // Luna AI service
  video: process.env.REACT_APP_DEBUG_VIDEO !== 'false', // Video playback

  /**
   * Checks if debugging is enabled for a specific area
   * @param area - Optional specific area to check (app, luna, video)
   * @returns true if debugging is enabled for the area, false otherwise
   *
   * IMPORTANT: Master switch overrides all granular controls
   * - If enabled=false, returns false regardless of granular settings
   * - If enabled=true and no area specified, returns true
   * - If enabled=true and area specified, returns that area's setting
   */
  isEnabled: (area?: 'app' | 'luna' | 'video'): boolean => {
    // Master switch OFF → all debugging disabled
    if (!debug.enabled) return false;

    // Master switch ON, no specific area → debugging enabled
    if (!area) return true;

    // Master switch ON, specific area → check that area's setting
    return debug[area];
  }
};

/**
 * Supabase configuration
 *
 * Canonical .env names: SUPABASE_URL, SUPABASE_ANON_KEY
 * craco.config.js maps them to REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_KEY
 * for CRA's DefinePlugin. Server-side code reads the canonical names directly.
 */
export const supabase = {
  url: process.env.REACT_APP_SUPABASE_URL || '',
  key: process.env.REACT_APP_SUPABASE_KEY || '',

  /**
   * Checks if Supabase is properly configured
   */
  isConfigured: (): boolean => {
    return Boolean(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_KEY);
  }
};

// SendGrid is no longer used - all email functionality uses Mailgun

/**
 * Stripe configuration
 *
 * Canonical .env name: STRIPE_PUBLISHABLE_KEY
 * craco.config.js maps it to REACT_APP_STRIPE_PUBLISHABLE_KEY for CRA.
 * Must match the environment (test/live) of STRIPE_SECRET_KEY in Netlify env.
 */
export const stripeConfig = {
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  isConfigured: (): boolean => Boolean(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY),
};

/**
 * App URLs configuration
 * External URLs for the Moon Manifest ecosystem
 *
 * REQUIRED: REACT_APP_APP_BASE_URL must be set in environment
 * - Netlify: Set via `netlify env:set REACT_APP_APP_BASE_URL`
 * - Local: Set in .env.local
 */
export const appUrls = {
  // Web app (mmwebapp-bmad) base URL - REQUIRED env var, no fallback
  appBaseUrl: process.env.REACT_APP_APP_BASE_URL || '',

  /**
   * Get the login URL for the web app
   */
  getLoginUrl: (): string => {
    return `${appUrls.appBaseUrl}/login`;
  },

  /**
   * Get the signup URL for the web app
   * Uses /app-signup for paid leads (not redirected to pricing)
   */
  getSignupUrl: (): string => {
    return `${appUrls.appBaseUrl}/app-signup`;
  },

  /**
   * Get the provision-access API URL for the web app
   */
  getProvisionAccessUrl: (): string => {
    return `${appUrls.appBaseUrl}/api/auth/provision-access`;
  }
};

/**
 * Mailgun configuration
 */
export const mailgun = {
  apiKey: process.env.MAILGUN_API_KEY || '',
  domain: process.env.MAILGUN_DOMAIN || 'mg.moonmanifest.com',
  sender: process.env.MAILGUN_VERIFIED_SENDER || 'Moon Manifest <postmaster@mg.moonmanifest.com>',
  
  /**
   * Checks if Mailgun is properly configured
   */
  isConfigured: (): boolean => {
    // For frontend, we don't need the API key since we use the Netlify function
    return true;
  }
};

/**
 * Logger utility for environment variable issues
 */
export const checkEnvironmentVariables = (): void => {
  // Check Supabase configuration
  if (!supabase.isConfigured()) {
    console.warn('Supabase environment variables are missing or incomplete.');
    
    if (!process.env.REACT_APP_SUPABASE_URL) {
      console.warn('Missing REACT_APP_SUPABASE_URL');
    }
    
    if (!process.env.REACT_APP_SUPABASE_KEY) {
      console.warn('Missing REACT_APP_SUPABASE_KEY');
    }
  }

  // Check App URL configuration
  if (!process.env.REACT_APP_APP_BASE_URL) {
    console.warn('Missing REACT_APP_APP_BASE_URL - app access features will not work');
  }

  // Check Stripe configuration
  if (!stripeConfig.isConfigured()) {
    console.warn('Missing REACT_APP_STRIPE_PUBLISHABLE_KEY - embedded checkout will not work');
  }

  // SendGrid is no longer used - all email functionality uses Mailgun

  // No need to check Mailgun in the frontend since it uses the Netlify function
  // which handles the API key configuration
};

// Export environment check function as default
export default checkEnvironmentVariables;
